import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Motion";
import { ACTIVITY_STATUS, canRegister } from "@/lib/statuses";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Activities({ onTalk, onRegister }) {
  const { t, lang, localized } = useLang();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/activities`);
        setActivities(data);
      } catch {
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <section id="activites" className="py-16 md:py-24 relative overflow-hidden" data-testid="activities-section">
      <div className="absolute inset-0 bg-muted/60" aria-hidden="true" />
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="max-w-2xl">
              <div className="chip">{t("activities.chip")}</div>
              <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-[1.05] text-foreground">
                {t("activities.title_our")}{" "}
                <span className="text-primary">{t("activities.title_actuelles")}</span>
              </h2>
              <p className="mt-4 text-lg text-foreground/75 leading-relaxed">
                {t("activities.subtitle")}
              </p>
            </div>
            <button
              onClick={onTalk}
              data-testid="activities-talk-btn"
              className="pill-btn bg-white border-2 border-foreground text-foreground hover:bg-foreground hover:text-background self-start"
            >
              {t("activities.talk_cta")}
            </button>
          </div>
        </Reveal>

        {loading ? (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-3xl bg-white border border-border h-[420px] animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="activities-grid">
            {activities.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.05}>
                <ActivityCard
                  activity={a}
                  title={localized(a, "title")}
                  benefit={localized(a, "benefit")}
                  onRegister={onRegister}
                  t={t}
                />
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ActivityCard({ activity, title, benefit, onRegister, t }) {
  const { lang } = useLang();
  const st = ACTIVITY_STATUS[activity.status] || ACTIVITY_STATUS.bientot;
  const registrable = canRegister(activity.status);
  const localizeAge = (v) =>
    lang === "nl" && typeof v === "string"
      ? v.replaceAll("ans", "jaar").replace("Adultes", "Volwassenen")
      : v;
  return (
    <article
      className="group rounded-3xl overflow-hidden bg-white border border-border hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(23,49,58,0.25)] transition-all h-full flex flex-col"
      data-testid={`activity-card-${activity.slug}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        {activity.image_url ? (
          <img
            src={activity.image_url}
            alt={title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="w-full h-full bg-muted" />
        )}
        <div
          className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold"
          style={{ backgroundColor: st.bg, color: st.fg }}
          data-testid={`activity-status-${activity.slug}`}
        >
          {t(st.labelKey)}
        </div>
      </div>
      <div className="p-5 md:p-6 flex-1 flex flex-col">
        <h3 className="font-heading text-xl leading-tight text-foreground">{title}</h3>
        <p className="mt-2 text-base text-foreground/75 leading-relaxed flex-1">{benefit}</p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
          <span className="px-2.5 py-1 rounded-full bg-muted text-foreground/70">{localizeAge(activity.age_range)}</span>
          <span className="px-2.5 py-1 rounded-full bg-muted text-foreground/70">{activity.language}</span>
        </div>
        <div className="mt-5">
          {registrable ? (
            <button
              onClick={() => onRegister({ ...activity, _localized_title: title })}
              data-testid={`activity-register-${activity.slug}`}
              className="pill-btn bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto justify-center"
            >
              {t("activities.register")}
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
          ) : (
            <div className="text-sm text-foreground/60 italic">{t("activities.soon")}</div>
          )}
        </div>
      </div>
    </article>
  );
}
